import { useApiUrl, useCustom, useNotification, useTranslate } from "@refinedev/core";
import { Button, Card, Col, Form, Row, Select, Steps, Upload, UploadProps, message, Modal, Input, Collapse, Radio, Table, Tag, Drawer, Descriptions, Space, Tooltip, Badge, theme } from "antd";
import type { StepProps } from "antd";
import { InboxOutlined, SendOutlined, DownloadOutlined, KeyOutlined, CopyOutlined, EyeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import Dragger from "antd/es/upload/Dragger";
import { MethodOptions } from "../../models/automation";
import { axiosInstance } from "../../authProvider";
import { useTenantSettings } from "../../hooks/tenant";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

export const AutomationPage = () => {
    const { token } = theme.useToken();
    const apiUrl = useApiUrl();
    const { open } = useNotification();
    const t = useTranslate();
    const [form] = Form.useForm();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [apiKey, setApiKey] = useState<string>("");
    const [isApiKeyModalVisible, setIsApiKeyModalVisible] = useState<boolean>(false);

    // Autotuner credentials (default from tenant settings)
    const tenantSettings = useTenantSettings();
    const [autotunerMastId, setAutotunerMastId] = useState<string>("");
    const [autotunerApiKey, setAutotunerApiKey] = useState<string>("");
    // IDs returned by an Autotuner decrypt, reused to pre-fill the encrypt panel
    const [autotunerDecryptIds, setAutotunerDecryptIds] = useState<{
        slave_id: string;
        mcu_id: string;
        ecu_id: number;
        model_id: number;
    } | null>(null);

    // Encryption panel states
    const [encryptFile, setEncryptFile] = useState<File | null>(null);
    const [encryptMethod, setEncryptMethod] = useState<string>("bflash");
    // Autotuner encrypt IDs (pre-filled from the last Autotuner decrypt, editable)
    const [encryptAutotunerSlaveId, setEncryptAutotunerSlaveId] = useState<string>("");
    const [encryptAutotunerMcuId, setEncryptAutotunerMcuId] = useState<string>("");
    const [encryptAutotunerEcuId, setEncryptAutotunerEcuId] = useState<string>("");
    const [encryptAutotunerModelId, setEncryptAutotunerModelId] = useState<string>("");
    const [isEncryptProcessing, setIsEncryptProcessing] = useState(false);
    const [encryptErrorMessage, setEncryptErrorMessage] = useState<string>("");
    const [encryptResponse, setEncryptResponse] = useState<{
        timestamp: string;
        name: string;
        encrypted_by: string;
        encrypted_by_master: boolean;
        hash: string;
        fileId: string;
        downloadUrl: string;
    } | null>(null);
    const [encryptLifetime, setEncryptLifetime] = useState<string>("");
    const [encryptSerialNumbers, setEncryptSerialNumbers] = useState<string>("");
    const [encryptCompress, setEncryptCompress] = useState<boolean>(true);

    // Analyse panel states
    const [analyseFile, setAnalyseFile] = useState<File | null>(null);
    const [isAnalyseProcessing, setIsAnalyseProcessing] = useState(false);
    const [analyseErrorMessage, setAnalyseErrorMessage] = useState<string>("");
    const [analyseResponse, setAnalyseResponse] = useState<any>(null);
    const [isAssembling, setIsAssembling] = useState<boolean>(false);
    const [analyseMode, setAnalyseMode] = useState<'upload' | 'decrypted'>('upload');
    const [decryptResponse, setDecryptResponse] = useState<{
        timestamp: string;
        name: string;
        encrypted_by: string;
        encrypted_by_master: boolean;
        hash: string;
        fileId: string;
        downloadUrl: string;
        autotuner?: {
            slave_id: string;
            mcu_id: string;
            ecu_id: number;
            model_id: number;
        };
    } | null>(null);
    const [stepsStatus, setStepsStatus] = useState<{
        decrypted: 'wait' | 'error' | 'finish' | 'process';
        analysed: 'wait' | 'error' | 'finish' | 'process';
        matched: 'wait' | 'error' | 'finish' | 'process';
        assembled: 'wait' | 'error' | 'finish' | 'process';
    }>({
        decrypted: 'wait',
        analysed: 'wait',
        matched: 'wait',
        assembled: 'wait'
    });

    // Drawer states
    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
    const [selectedFileData, setSelectedFileData] = useState<any>(null);

    // Pre-fill Autotuner credentials from tenant settings (user can still override)
    useEffect(() => {
        if (typeof tenantSettings?.autotunerMastId === "string") {
            setAutotunerMastId(tenantSettings.autotunerMastId);
        }
        if (typeof tenantSettings?.autotunerApiKey === "string") {
            setAutotunerApiKey(tenantSettings.autotunerApiKey);
        }
    }, [tenantSettings]);

    const fileUploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        beforeUpload: (file) => {
            const isValidType = file.type === 'application/octet-stream' ||
                file.type === 'application/binary' ||
                file.name.endsWith('.bin') ||
                file.name.endsWith('.hex') ||
                file.name.endsWith('.s19') || true;

            if (!isValidType) {
                message.error(t('automation.errors.invalidFileType'));
                return false;
            }

            const isLt10M = file.size / 1024 / 1024 < MAX_FILE_SIZE;
            if (!isLt10M) {
                message.error(t('automation.errors.fileTooLarge'));
                return false;
            }

            // Store the selected file instead of uploading immediately
            setSelectedFile(file);
            message.success(t('automation.messages.fileSelected', { fileName: file.name }));
            return false; // Prevent automatic upload
        },
        onChange(info) {
            // Handle any other changes if needed
            console.log('File selection changed:', info.fileList);
        },
    };

    const analyseFileUploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        beforeUpload: (file) => {
            const isValidType = file.type === 'application/octet-stream' ||
                file.type === 'application/binary' ||
                file.name.endsWith('.bin') ||
                file.name.endsWith('.hex') ||
                file.name.endsWith('.s19') || true;

            if (!isValidType) {
                message.error(t('automation.errors.invalidFileType'));
                return false;
            }

            const isLt10M = file.size / 1024 / 1024 < MAX_FILE_SIZE;
            if (!isLt10M) {
                message.error(t('automation.errors.fileTooLarge'));
                return false;
            }

            // Store the selected file for analysis
            setAnalyseFile(file);
            message.success(t('automation.messages.fileSelectedForAnalysis', { fileName: file.name }));
            return false; // Prevent automatic upload
        },
        onChange(info) {
            // Handle any other changes if needed
            console.log('Analysis file selection changed:', info.fileList);
        },
    };

    const encryptFileUploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        beforeUpload: (file) => {
            const isValidType = file.type === 'application/octet-stream' ||
                file.type === 'application/binary' ||
                file.name.endsWith('.bin') ||
                file.name.endsWith('.hex') ||
                file.name.endsWith('.s19') || true;

            if (!isValidType) {
                message.error(t('automation.errors.invalidFileType'));
                return false;
            }

            const isLt10M = file.size / 1024 / 1024 < MAX_FILE_SIZE;
            if (!isLt10M) {
                message.error(t('automation.errors.fileTooLarge'));
                return false;
            }

            // Store the selected file for encryption
            setEncryptFile(file);
            message.success(t('automation.messages.fileSelectedForEncryption', { fileName: file.name }));
            return false; // Prevent automatic upload
        },
        onChange(info) {
            console.log('Encryption file selection changed:', info.fileList);
        },
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            message.error(t('automation.errors.selectFileFirst'));
            return;
        }

        if (!selectedMethod) {
            message.error(t('automation.errors.selectMethod'));
            return;
        }

        setIsProcessing(true);
        setDecryptResponse(null);
        setErrorMessage("");
        setStepsStatus({
            decrypted: 'process',
            analysed: 'wait',
            matched: 'wait',
            assembled: 'wait'
        });

        try {
            // Prepare FormData with file and method
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('method', selectedMethod);

            const isAutotuner = selectedMethod.toLowerCase().includes('autotuner');
            if (isAutotuner) {
                // Autotuner credentials default to tenant settings; only send when set
                if (autotunerMastId.trim()) {
                    formData.append('autotunerMastId', autotunerMastId);
                }
                if (autotunerApiKey.trim()) {
                    formData.append('autotunerApiKey', autotunerApiKey);
                }
                console.log('Autotuner credentials:', formData.get('autotunerMastId'), formData.get('autotunerApiKey'));
            } else if (apiKey.trim()) {
                formData.append('bFlashApiKey', apiKey);
            }

            // Call the /decrypt endpoint with file as FormData
            const response = await axiosInstance.post('/file-decrypter/decrypt', formData);

            const result = response.data;
            console.log('Decrypt successful:', result);

            setDecryptResponse(result);
            // Capture Autotuner IDs so the encrypt panel can reuse them
            if (result?.autotuner) {
                setAutotunerDecryptIds(result.autotuner);
                setEncryptAutotunerSlaveId(result.autotuner.slave_id ?? "");
                setEncryptAutotunerMcuId(result.autotuner.mcu_id ?? "");
                setEncryptAutotunerEcuId(result.autotuner.ecu_id != null ? String(result.autotuner.ecu_id) : "");
                setEncryptAutotunerModelId(result.autotuner.model_id != null ? String(result.autotuner.model_id) : "");
            }
            setStepsStatus(prev => ({
                ...prev,
                decrypted: 'finish'
            }));

            message.success(t('automation.messages.decryptSuccess'));
        } catch (error: any) {
            console.error('Decrypt error:', error);

            // Check if error response contains API key error
            if (error.response?.data?.errors?.apiKey === "adminMustProvideApiKey") {
                setErrorMessage(t('automation.errors.adminMustProvideApiKey'));
            } else {
                setErrorMessage(t('automation.errors.decryptFailed'));
            }

            setStepsStatus(prev => ({
                ...prev,
                decrypted: 'error'
            }));

            message.error(t('automation.errors.decryptFailed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownload = () => {
        if (decryptResponse?.downloadUrl) {
            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = decryptResponse.downloadUrl;
            link.download = decryptResponse.name || 'decrypted_file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success(t('automation.messages.downloadStarted'));
        }
    };

    const handleAnalyseDownload = () => {
        if (analyseResponse) {
            // Create a JSON blob from the analysis response
            const jsonString = JSON.stringify(analyseResponse, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = 'analysis-results.json';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            URL.revokeObjectURL(url);
            message.success(t('automation.messages.analysisDownloadStarted'));
        }
    };

    const handleApiKeyClick = () => {
        setIsApiKeyModalVisible(true);
    };

    const handleApiKeySave = () => {
        setIsApiKeyModalVisible(false);
        message.success(t('automation.messages.apiKeySaved'));
    };

    const handleApiKeyCancel = () => {
        setIsApiKeyModalVisible(false);
    };

    const handleAnalyseSubmit = async () => {
        if (analyseMode === 'upload' && !analyseFile) {
            message.error(t('automation.errors.selectFileForAnalysis'));
            return;
        }

        if (analyseMode === 'decrypted' && !decryptResponse?.fileId) {
            message.error(t('automation.errors.noDecryptedFileId'));
            return;
        }

        setIsAnalyseProcessing(true);
        setAnalyseErrorMessage("");
        setAnalyseResponse(null);

        try {
            let response;

            if (analyseMode === 'upload') {
                // Prepare FormData with file for analysis
                const formData = new FormData();
                formData.append('file', analyseFile!);
                response = await axiosInstance.post('/file-analyzer/analyze', formData);
            } else {
                // Use decrypted fileId parameter
                response = await axiosInstance.post('/file-analyzer/analyze', {
                    fileId: decryptResponse!.fileId
                });
            }

            const result = response.data;
            console.log('Analysis successful:', result);

            setAnalyseResponse(result);
            setStepsStatus(prev => ({
                ...prev,
                analysed: 'finish'
            }));
            message.success(t('automation.messages.analysisSuccess'));
        } catch (error: any) {
            console.error('Analysis error:', error);

            setAnalyseErrorMessage(t('automation.errors.analysisFailed'));
            setStepsStatus(prev => ({
                ...prev,
                analysed: 'error'
            }));
            message.error(t('automation.errors.analysisFailed'));
        } finally {
            setIsAnalyseProcessing(false);
        }
    };

    // Helper functions for drawer
    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'analyzed':
                return 'green';
            case 'pending':
                return 'orange';
            case 'error':
                return 'red';
            default:
                return 'default';
        }
    };

    const getStatusText = (status: string) => {
        if (!status) return t('automation.status.unknown');
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success(t('automation.messages.copiedToClipboard'));
        }).catch(() => {
            message.error(t('automation.errors.copyFailed'));
        });
    };

    // Transform analysis response projects into tree table data
    const getTableData = () => {
        if (!analyseResponse?.projects || !Array.isArray(analyseResponse.projects)) {
            return [];
        }

        const treeData: Array<{
            key: string;
            projectRef: string;
            type: string;
            tags: string[];
            fileData: any;
            children?: Array<{
                key: string;
                projectRef: string;
                type: string;
                tags: string[];
                fileData: any;
            }>;
        }> = [];

        analyseResponse.projects.forEach((project: any) => {
            if (project.files && Array.isArray(project.files)) {
                const children = project.files.map((file: any) => ({
                    key: file._id || `${project.projectRef}-${file.type}`,
                    projectRef: project.projectRef,
                    type: file.type || '',
                    tags: file.tags || [],
                    fileData: file
                }));

                treeData.push({
                    key: `project-${project.projectRef}`,
                    projectRef: project.projectRef,
                    type: '',
                    tags: [],
                    fileData: null,
                    children: children
                });
            }
        });

        return treeData;
    };

    const handleRowClick = (record: any) => {
        setSelectedFileData(record.fileData);
        setDrawerVisible(true);
    };

    const handleFileDownload = async () => {
        if (selectedFileData?.downloadUrl) {
            try {
                // Handle relative URLs by prepending the API URL
                let downloadUrl = selectedFileData.downloadUrl;
                if (downloadUrl.startsWith('/')) {
                    downloadUrl = `${apiUrl}${downloadUrl}`;
                }

                // Use axiosInstance to handle authenticated requests
                const response = await axiosInstance.get(downloadUrl, {
                    responseType: 'blob',
                });

                // Create a blob URL and trigger download
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = selectedFileData.binaryPath?.split('/').pop() || selectedFileData.type || 'file';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                message.success(t('automation.messages.downloadStarted'));
            } catch (error: any) {
                console.error('Download error:', error);
                message.error(t('automation.errors.downloadFailed'));
            }
        } else {
            message.error(t('automation.errors.downloadUrlNotAvailable'));
        }
    };

    const handleEncryptSubmit = async () => {
        if (!encryptFile) {
            message.error(t('automation.errors.selectFileFirst'));
            return;
        }

        if (!encryptMethod) {
            message.error(t('automation.errors.selectMethod'));
            return;
        }

        setIsEncryptProcessing(true);
        setEncryptResponse(null);
        setEncryptErrorMessage("");

        try {
            // Prepare FormData with file and method
            const formData = new FormData();
            formData.append('file', encryptFile);
            formData.append('method', encryptMethod);

            const isAutotuner = encryptMethod.toLowerCase().includes('autotuner');
            if (isAutotuner) {
                // Autotuner credentials default to tenant settings; IDs come from a prior decrypt
                if (autotunerMastId.trim()) {
                    formData.append('autotunerMastId', autotunerMastId);
                }
                if (autotunerApiKey.trim()) {
                    formData.append('autotunerApiKey', autotunerApiKey);
                }
                if (encryptAutotunerSlaveId.trim()) {
                    formData.append('autotunerSlaveId', encryptAutotunerSlaveId);
                }
                if (encryptAutotunerMcuId.trim()) {
                    formData.append('autotunerMcuId', encryptAutotunerMcuId);
                }
                if (encryptAutotunerEcuId.trim()) {
                    formData.append('autotunerEcuId', encryptAutotunerEcuId);
                }
                if (encryptAutotunerModelId.trim()) {
                    formData.append('autotunerModelId', encryptAutotunerModelId);
                }
            } else {
                // bFlash-only parameters
                if (apiKey.trim()) {
                    formData.append('bFlashApiKey', apiKey);
                }
                if (encryptLifetime.trim()) {
                    formData.append('lifetime', encryptLifetime);
                }
                if (encryptSerialNumbers.trim()) {
                    formData.append('serial_numbers', encryptSerialNumbers);
                }
                formData.append('compress', encryptCompress.toString());
            }

            // Call the /encrypt endpoint with file as FormData
            const response = await axiosInstance.post('/file-encrypter/encrypt', formData);

            const result = response.data;
            console.log('Encrypt successful:', result);

            setEncryptResponse(result);
            message.success(t('automation.messages.encryptSuccess'));
        } catch (error: any) {
            console.error('Encrypt error:', error);

            // Check if error response contains API key error
            if (error.response?.data?.errors?.apiKey === "adminMustProvideApiKey") {
                setEncryptErrorMessage(t('automation.errors.adminMustProvideApiKey'));
            } else {
                setEncryptErrorMessage(t('automation.errors.encryptFailed'));
            }

            message.error(t('automation.errors.encryptFailed'));
        } finally {
            setIsEncryptProcessing(false);
        }
    };

    const handleEncryptDownload = () => {
        if (encryptResponse?.downloadUrl) {
            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = encryptResponse.downloadUrl;
            link.download = encryptResponse.name
                || (encryptMethod.toLowerCase().includes('autotuner')
                    ? 'encrypted_file.slave'
                    : 'encrypted_file.bin');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success(t('automation.messages.downloadStarted'));
        } else {
            message.error(t('automation.errors.downloadUrlNotAvailable'));
        }
    };

    const handleAssemble = async (record: any) => {
        if (!analyseFile) {
            message.error(t('automation.errors.noAnalyzedFile'));
            return;
        }

        setIsAssembling(true);
        try {
            // Step 1: Upload analyseFile to /files/upload
            const formData = new FormData();
            formData.append('file', analyseFile);
            
            const uploadResponse = await axiosInstance.post('/files/upload', formData);
            const fileId = uploadResponse.data.file.id;
            
            // Step 1.5: Analyze the uploaded file to get analysis data
            let uploadedFileAnalysis = null;
            try {
                const analysisResponse = await axiosInstance.post('/file-analyzer/analyze', {
                    fileId: fileId
                });
                uploadedFileAnalysis = analysisResponse.data;
                console.log('Uploaded file analysis:', JSON.stringify(uploadedFileAnalysis, null, 2));
            } catch (error: any) {
                console.warn('Failed to analyze uploaded file, will try to use existing analysis:', error);
            }

            // Step 2: Find the Original file in the same projectRef
            const project = analyseResponse?.projects?.find((p: any) => p.projectRef === record.projectRef);
            if (!project) {
                message.error(t('automation.errors.projectNotFound'));
                setIsAssembling(false);
                return;
            }

            const originalFile = project.files?.find((f: any) => f.type === 'Original');
            if (!originalFile) {
                message.error(t('automation.errors.originalFileNotFound'));
                setIsAssembling(false);
                return;
            }

            // Step 3: Extract projectInfo from originalFile
            const projectInfo = {
                vehicleType: originalFile.vehicleType,
                vehicleProducer: originalFile.vehicleProducer,
                vehicleSeries: originalFile.vehicleSeries,
                vehicleBuild: originalFile.vehicleBuild,
                vehicleModel: originalFile.vehicleModel,
                vehicleModelyear: originalFile.vehicleModelyear,
                ecuUse: originalFile.ecuUse,
                ecuProducer: originalFile.ecuProducer,
                ecuBuild: originalFile.ecuBuild,
                ecuSoftwareVersion: originalFile.ecuSoftwareVersion,
                engineType: originalFile.engineType,
                engineDisplacement: originalFile.engineDisplacement,
                readingHardware: originalFile.readingHardware,
                projectType: originalFile.projectType
            };

            // Step 4: Transform analysis data for target_src
            // The analysis response has Blocks at the top level in the correct format
            let targetSrcAnalysis = null;
            
            // Prefer analysis from the newly analyzed uploaded file, fallback to analyseResponse
            const analysisSource = uploadedFileAnalysis || analyseResponse;
            
            // The analysis endpoint returns Blocks at the top level with the correct structure
            if (analysisSource?.Blocks && Array.isArray(analysisSource.Blocks)) {
                // The Blocks are already in the correct format for target_src
                targetSrcAnalysis = {
                    Blocks: analysisSource.Blocks.map((block: any) => ({
                        Block: {
                            BlockIdentifier: block.Block?.BlockIdentifier,
                            BlockName: block.Block?.BlockName,
                            PhysicalAddress: block.Block?.PhysicalAddress,
                            LogicalAddress: block.Block?.LogicalAddress,
                            PhysicalToLogicalOffset: block.Block?.PhysicalToLogicalOffset,
                            Endblock_PhysicalAddress: block.Block?.Endblock_PhysicalAddress,
                            Endblock_LogicalAddress: block.Block?.Endblock_LogicalAddress,
                            BlockSize: block.Block?.BlockSize,
                            BlockType: block.Block?.BlockType
                        },
                        Hash: block.Hash
                    }))
                };
            } else {
                console.warn('No Blocks found in analysis response for target_src');
                console.log('Analysis source:', JSON.stringify(analysisSource, null, 2));
            }

            // Step 5: Transform analysis data for patch_src (from originalFile)
            let patchSrcAnalysis = null;
            if (originalFile.analysis?.Blocks) {
                patchSrcAnalysis = {
                    Blocks: originalFile.analysis.Blocks.map((block: any) => ({
                        Block: {
                            BlockIdentifier: 96,//block.Block?.BlockIdentifier,
                            //BlockName: "dataset0",
                            BlockSize: Number(block.Block?.length) || Number(block.Block?.BlockSize),
                            BlockType: Number(block.Block?.type) || Number(block.Block?.BlockType),
                            PhysicalAddress: block.Block?.physicalAddress || block.Block?.PhysicalAddress,
                            LogicalAddress: block.Block?.logicalAddress || block.Block?.LogicalAddress,
                            PhysicalToLogicalOffset: block.Block?.physicalToLogicalOffset || block.Block?.PhysicalToLogicalOffset,
                            Endblock_PhysicalAddress: block.Block?.endblockPhysicalAddress || block.Block?.Endblock_PhysicalAddress,
                            Endblock_LogicalAddress: block.Block?.endblockLogicalAddress || block.Block?.Endblock_LogicalAddress
                        },
                        Hash: block.Hash || block.hash
                    }))
                };
            } else if (originalFile.blocks) {
                // Fallback: use blocks array if analysis.Blocks doesn't exist
                patchSrcAnalysis = {
                    Blocks: originalFile.blocks.map((block: any) => ({
                        Block: {
                            BlockIdentifier: 96, //block.id,
                            //BlockName: "dataset0",
                            BlockSize: Number(block.length),
                            BlockType: Number(block.type),
                            PhysicalAddress: block.physicalAddress,
                            LogicalAddress: block.logicalAddress,
                            PhysicalToLogicalOffset: block.physicalToLogicalOffset,
                            Endblock_PhysicalAddress: block.endblockPhysicalAddress,
                            Endblock_LogicalAddress: block.endblockLogicalAddress
                        },
                        Hash: block.hash
                    }))
                };
            }

            // Step 6: Transform analysis data for patch_modified (from clicked file)
            const clickedFile = record.fileData;
            let patchModifiedAnalysis = null;
            if (clickedFile.analysis?.Blocks) {
                patchModifiedAnalysis = {
                    Blocks: clickedFile.analysis.Blocks.map((block: any) => ({
                        Block: {
                            BlockIdentifier: block.Block?.id || block.Block?.BlockName || block.Block?.BlockIdentifier,
                            BlockSize: Number(block.Block?.length) || Number(block.Block?.BlockSize),
                            BlockType: Number(block.Block?.type) || Number(block.Block?.BlockType),
                            PhysicalAddress: block.Block?.physicalAddress || block.Block?.PhysicalAddress,
                            LogicalAddress: block.Block?.logicalAddress || block.Block?.LogicalAddress,
                            PhysicalToLogicalOffset: block.Block?.physicalToLogicalOffset || block.Block?.PhysicalToLogicalOffset,
                            Endblock_PhysicalAddress: block.Block?.endblockPhysicalAddress || block.Block?.Endblock_PhysicalAddress,
                            Endblock_LogicalAddress: block.Block?.endblockLogicalAddress || block.Block?.Endblock_LogicalAddress
                        },
                        Hash: block.Hash || block.hash
                    }))
                };
            } else if (clickedFile.blocks) {
                // Fallback: use blocks array if analysis.Blocks doesn't exist
                patchModifiedAnalysis = {
                    Blocks: clickedFile.blocks.map((block: any) => ({
                        Block: {
                            BlockIdentifier: block.id,
                            BlockSize: Number(block.length),
                            BlockType: Number(block.type),
                            PhysicalAddress: block.physicalAddress,
                            LogicalAddress: block.logicalAddress,
                            PhysicalToLogicalOffset: block.physicalToLogicalOffset,
                            Endblock_PhysicalAddress: block.endblockPhysicalAddress,
                            Endblock_LogicalAddress: block.endblockLogicalAddress
                        },
                        Hash: block.hash
                    }))
                };
            }

            // Step 7: Construct the payload
            const payload = {
                projectInfo: projectInfo,
                files: [
                    {
                        type: "target_src",
                        fileId: fileId,
                        analysis: targetSrcAnalysis
                    },
                    {
                        type: "patch_src",
                        downloadUrl: originalFile.downloadUrl,
                        analysis: patchSrcAnalysis
                    },
                    {
                        type: "patch_modified",
                        downloadUrl: clickedFile.downloadUrl,
                        analysis: patchModifiedAnalysis
                    }
                ]
            };

            // Step 8: Call the assemble endpoint
            const assembleResponse = await axiosInstance.post('/file-assembler/assemble', payload);
            
            console.log('Assemble successful:', assembleResponse.data);
            
            // Step 9: Download the assembled file if ecuxFilePath is provided
            if (assembleResponse.data?.ecuxFilePath) {
                try {
                    const ecuxFilePath = assembleResponse.data.ecuxFilePath;
                    
                    // Use fetch for external URLs (DigitalOcean Spaces) to avoid baseURL issues
                    const downloadResponse = await fetch(ecuxFilePath);
                    
                    if (!downloadResponse.ok) {
                        throw new Error(`Download failed with status: ${downloadResponse.status}`);
                    }

                    // Create a blob from the response
                    const blob = await downloadResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    
                    // Extract filename from URL or use default
                    const urlParts = ecuxFilePath.split('/');
                    const fileName = urlParts[urlParts.length - 1].split('?')[0] || 'assembled_file.ecux';
                    link.download = fileName;
                    
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    message.success(t('automation.messages.assembleSuccessDownloaded'));
                } catch (downloadError: any) {
                    console.error('Download error:', downloadError);
                    // Fallback: open the URL directly in a new tab
                    window.open(assembleResponse.data.ecuxFilePath, '_blank');
                    message.warning(t('automation.messages.assembleSuccessOpenTab'));
                }
            } else {
                message.success(t('automation.messages.assembleSuccess'));
            }
            
            setStepsStatus(prev => ({
                ...prev,
                assembled: 'finish'
            }));
        } catch (error: any) {
            console.error('Assemble error:', error);
            message.error(error.response?.data?.message || t('automation.errors.assembleFailed'));
            setStepsStatus(prev => ({
                ...prev,
                assembled: 'error'
            }));
        } finally {
            setIsAssembling(false);
        }
    };

    const tableColumns = [
        {
            title: t('automation.table.projectRef'),
            dataIndex: 'projectRef',
            key: 'projectRef',
            render: (text: string, record: any) => {
                // Parent row (project)
                if (!record.fileData) {
                    return (
                        <span className="font-semibold">
                            {text} ({record.children?.length || 0} {t('automation.table.files')})
                        </span>
                    );
                }
                // Child row (file)
                return text;
            },
        },
        {
            title: t('automation.table.type'),
            dataIndex: 'type',
            key: 'type',
            render: (text: string, record: any) => {
                // Parent row - show empty or count
                if (!record.fileData) {
                    return <span className="text-gray-400">-</span>;
                }
                return text || '-';
            },
        },
        {
            title: t('automation.table.tags'),
            dataIndex: 'tags',
            key: 'tags',
            render: (tags: string[], record: any) => {
                // Parent row - show empty
                if (!record.fileData) {
                    return <span className="text-gray-400">-</span>;
                }
                // Child row
                return (
                    <div>
                        {tags && tags.length > 0 ? (
                            tags.map((tag, index) => (
                                <Tag key={index} className="mb-1">
                                    {tag}
                                </Tag>
                            ))
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: t('automation.table.actions'),
            key: 'actions',
            render: (_: any, record: any) => {
                // Parent row - no actions
                if (!record.fileData) {
                    return <span className="text-gray-400">-</span>;
                }
                // Child row - show actions
                return (
                    <Space>
                        <Button
                            icon={<InfoCircleOutlined />}
                            onClick={() => handleRowClick(record)}
                        >
                        </Button>
                        {record.fileData?.downloadUrl && (
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={() => {
                                    setSelectedFileData(record.fileData);
                                    handleFileDownload();
                                }}
                            >
                            </Button>
                        )}
                        {analyseFile && (
                            <Button
                                icon={<SendOutlined />}
                                onClick={() => handleAssemble(record)}
                                loading={isAssembling}
                                disabled={isAssembling}
                            >
                                {t('automation.buttons.assemble')}
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    const steps: StepProps[] = [
        {
            title: t('automation.steps.decrypted'),
            status: stepsStatus.decrypted,
            description: stepsStatus.decrypted === 'finish' ? (
                <div>
                    <div className="mb-2">{t('automation.messages.decryptSuccess')}</div>
                    {decryptResponse && (
                        <div className="mt-2">
                            <Button
                                type="primary"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                                className="mb-1"
                            >
                                {t('automation.buttons.downloadFile')}
                            </Button>
                            <div className="text-xs text-gray-500">
                                {decryptResponse.name}
                            </div>
                        </div>
                    )}
                </div>
            ) :
                stepsStatus.decrypted === 'error' ? (errorMessage || t('automation.errors.decryptFailed')) :
                    stepsStatus.decrypted === 'process' ? t('automation.status.decrypting') : t('automation.status.waitingToDecrypt')
        },
        {
            title: t('automation.steps.analysedMatched'),
            status: stepsStatus.analysed,
            description: stepsStatus.analysed === 'finish' ? (
                <div>
                    <div className="mb-2">{t('automation.messages.analysisSuccess')}</div>
                    {analyseResponse && (
                        <div className="mt-2">
                            <Button
                                type="primary"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={handleAnalyseDownload}
                                className="mb-1"
                            >
                                {t('automation.buttons.downloadResults')}
                            </Button>
                            <div className="text-xs text-gray-500">
                                analysis-results.json
                            </div>
                        </div>
                    )}
                </div>
            ) :
                stepsStatus.analysed === 'error' ? (analyseErrorMessage || t('automation.errors.analysisFailed')) :
                    stepsStatus.analysed === 'process' ? t('automation.status.analyzing') : t('automation.status.analysisPending')
        },
        {
            title: t('automation.steps.assembled'),
            status: stepsStatus.assembled,
            description: t('automation.status.assemblyPending')
        }
    ];

    return (
        <div className="p-6">
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                    <Row gutter={[0, 24]}>
                        <Col xs={24}>
                            <Card title={t('automation.cards.decrypt')} className="shadow-lg">
                                <Form form={form} layout="vertical">
                                    <Form.Item
                                        label=""
                                        required
                                        className="mb-0"
                                        tooltip={t('automation.tooltips.selectBinaryFile')}
                                    >
                                        <Dragger
                                            {...fileUploadProps}
                                            className="!py-4 !px-2 !min-h-[80px]"
                                        >
                                            <p className="ant-upload-drag-icon !text-2xl !mb-2">
                                                <InboxOutlined />
                                            </p>
                                            <p className="ant-upload-text !text-sm !mb-1">
                                                {t('automation.upload.clickOrDrag')}
                                            </p>
                                            {selectedFile && (
                                                <p className="ant-upload-hint text-blue-600 !text-xs">
                                                    {t('automation.upload.selected', { fileName: selectedFile.name })}
                                                </p>
                                            )}
                                        </Dragger>
                                    </Form.Item>

                                    <Form.Item
                                        label={t('automation.fields.method')}
                                        required
                                        tooltip={t('automation.tooltips.selectMethod')}
                                    >
                                        <Select
                                            placeholder={t('automation.placeholders.selectMethod')}
                                            allowClear
                                            onChange={setSelectedMethod}
                                            options={MethodOptions}
                                            className="w-full"
                                        />
                                    </Form.Item>

                                    {selectedMethod.toLowerCase().includes('autotuner') && (
                                        <>
                                            <Form.Item
                                                label={t('automation.fields.autotunerMastId')}
                                                tooltip={t('automation.tooltips.autotunerTenantDefault')}
                                            >
                                                <Input
                                                    value={autotunerMastId}
                                                    onChange={(e) => setAutotunerMastId(e.target.value)}
                                                    placeholder={t('automation.placeholders.autotunerMastId')}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label={t('automation.fields.autotunerApiKey')}
                                                tooltip={t('automation.tooltips.autotunerTenantDefault')}
                                            >
                                                <Input.Password
                                                    value={autotunerApiKey}
                                                    onChange={(e) => setAutotunerApiKey(e.target.value)}
                                                    placeholder={t('automation.placeholders.autotunerApiKey')}
                                                />
                                            </Form.Item>
                                            <p className="text-xs text-gray-500 -mt-2 mb-2">
                                                {t('automation.autotunerTenantDefault')}
                                            </p>
                                        </>
                                    )}

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<SendOutlined />}
                                            onClick={handleSubmit}
                                            loading={isProcessing}
                                            disabled={!selectedFile || !selectedMethod}
                                            className="w-full"
                                        >
                                            {t('automation.buttons.processFile')}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>

                        <Col xs={24}>
                            <Card title={t('automation.cards.analyse')} className="shadow-lg">
                                <Form layout="vertical">
                                    <Form.Item
                                        label={t('automation.fields.analysisMode')}
                                        tooltip={t('automation.tooltips.analysisMode')}
                                        className="mb-0"
                                    >
                                        <Radio.Group
                                            value={analyseMode}
                                            onChange={(e) => setAnalyseMode(e.target.value)}
                                            className="w-full"
                                        >
                                            <Radio value="upload">{t('automation.radio.uploadFile')}</Radio>
                                            <Radio value="decrypted" disabled={!decryptResponse?.fileId}>
                                                {t('automation.radio.useDecryptedFile')}
                                                {decryptResponse?.fileId && (
                                                    <span className="text-green-600 ml-2">
                                                        ({decryptResponse.fileId})
                                                    </span>
                                                )}
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    {analyseMode === 'upload' ? (
                                        <Form.Item
                                            label=""
                                            required
                                            tooltip={t('automation.tooltips.selectBinaryFileForAnalysis')}
                                            className="mb-0"
                                        >
                                            <Dragger
                                                {...analyseFileUploadProps}
                                                className="!py-4 !px-2 !min-h-[80px]"
                                            >
                                                <p className="ant-upload-drag-icon !text-2xl !mb-2">
                                                    <InboxOutlined />
                                                </p>
                                                <p className="ant-upload-text !text-sm !mb-1">
                                                    {t('automation.upload.clickOrDrag')}
                                                </p>
                                                {analyseFile && (
                                                    <p className="ant-upload-hint text-blue-600 !text-xs">
                                                        {t('automation.upload.selected', { fileName: analyseFile.name })}
                                                    </p>
                                                )}
                                            </Dragger>
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            label={t('automation.fields.decryptedFile')}
                                            tooltip={t('automation.tooltips.usingDecryptedFileId')}
                                        >
                                            <div className="p-4 bg-green-50 border border-green-200 rounded">
                                                <div className="text-green-800 font-medium">
                                                    {t('automation.messages.usingDecryptedFileId', { fileId: decryptResponse?.fileId })}
                                                </div>
                                                <div className="text-green-600 text-sm mt-1">
                                                    {t('automation.fields.file')}: {decryptResponse?.name}
                                                </div>
                                            </div>
                                        </Form.Item>
                                    )}

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<SendOutlined />}
                                            onClick={handleAnalyseSubmit}
                                            loading={isAnalyseProcessing}
                                            disabled={analyseMode === 'upload' ? !analyseFile : !decryptResponse?.fileId}
                                            className="w-full"
                                        >
                                            {t('automation.buttons.analyzeFile')}
                                        </Button>
                                    </Form.Item>

                                    {analyseErrorMessage && (
                                        <div className="text-red-500 text-sm mt-2">
                                            {analyseErrorMessage}
                                        </div>
                                    )}

                                    {analyseResponse && (
                                        <div className="mt-4">
                                            <Collapse
                                                items={[
                                                    {
                                                        key: '1',
                                                        label: t('automation.collapse.analysisResponse'),
                                                        children: (
                                                            <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                                                                {JSON.stringify(analyseResponse, null, 2)}
                                                            </pre>
                                                        ),
                                                    },
                                                ]}
                                                size="small"
                                            />
                                        </div>
                                    )}
                                </Form>
                            </Card>
                        </Col>

                        <Col xs={24}>
                            <Card title={t('automation.cards.encrypt')} className="shadow-lg">
                                <Form layout="vertical">
                                    <Form.Item
                                        label=""
                                        required
                                        className="mb-0"
                                        tooltip={t('automation.tooltips.selectBinaryFile')}
                                    >
                                        <Dragger
                                            {...encryptFileUploadProps}
                                            className="!py-4 !px-2 !min-h-[80px]"
                                        >
                                            <p className="ant-upload-drag-icon !text-2xl !mb-2">
                                                <InboxOutlined />
                                            </p>
                                            <p className="ant-upload-text !text-sm !mb-1">
                                                {t('automation.upload.clickOrDrag')}
                                            </p>
                                            {encryptFile && (
                                                <p className="ant-upload-hint text-blue-600 !text-xs">
                                                    {t('automation.upload.selected', { fileName: encryptFile.name })}
                                                </p>
                                            )}
                                        </Dragger>
                                    </Form.Item>

                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label={t('automation.fields.method')}
                                                required
                                                tooltip={t('automation.tooltips.selectMethod')}
                                            >
                                                <Select
                                                    placeholder={t('automation.placeholders.selectMethod')}
                                                    value={encryptMethod}
                                                    onChange={setEncryptMethod}
                                                    className="w-full"
                                                    options={[
                                                        { label: 'bFlash', value: 'bflash' },
                                                        { label: 'Autotuner', value: 'autotuner' }
                                                    ]}
                                                />
                                            </Form.Item>
                                        </Col>
                                        {!encryptMethod.toLowerCase().includes('autotuner') && (
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={t('automation.fields.compress')}
                                                    tooltip={t('automation.tooltips.compress')}
                                                >
                                                    <Radio.Group
                                                        value={encryptCompress}
                                                        onChange={(e) => setEncryptCompress(e.target.value)}
                                                        className="w-full"
                                                    >
                                                        <Radio value={true}>{t('automation.yes')}</Radio>
                                                        <Radio value={false}>{t('automation.no')}</Radio>
                                                    </Radio.Group>
                                                </Form.Item>
                                            </Col>
                                        )}
                                    </Row>

                                    {!encryptMethod.toLowerCase().includes('autotuner') ? (
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={t('automation.fields.lifetime')}
                                                    tooltip={t('automation.tooltips.lifetime')}
                                                >
                                                    <Input
                                                        placeholder={t('automation.placeholders.lifetime')}
                                                        value={encryptLifetime}
                                                        onChange={(e) => setEncryptLifetime(e.target.value)}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={t('automation.fields.serialNumbers')}
                                                    tooltip={t('automation.tooltips.serialNumbers')}
                                                >
                                                    <Input
                                                        placeholder={t('automation.placeholders.serialNumbers')}
                                                        value={encryptSerialNumbers}
                                                        onChange={(e) => setEncryptSerialNumbers(e.target.value)}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <>
                                            <Row gutter={16}>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item
                                                        label={t('automation.fields.autotunerMastId')}
                                                        tooltip={t('automation.tooltips.autotunerTenantDefault')}
                                                    >
                                                        <Input
                                                            value={autotunerMastId}
                                                            onChange={(e) => setAutotunerMastId(e.target.value)}
                                                            placeholder={t('automation.placeholders.autotunerMastId')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item
                                                        label={t('automation.fields.autotunerApiKey')}
                                                        tooltip={t('automation.tooltips.autotunerTenantDefault')}
                                                    >
                                                        <Input.Password
                                                            value={autotunerApiKey}
                                                            onChange={(e) => setAutotunerApiKey(e.target.value)}
                                                            placeholder={t('automation.placeholders.autotunerApiKey')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <p className="text-xs text-gray-500 -mt-2 mb-2">
                                                {autotunerDecryptIds
                                                    ? t('automation.autotunerIdsFromDecrypt')
                                                    : t('automation.autotunerIdsManual')}
                                            </p>
                                            <Row gutter={16}>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label={t('automation.fields.autotunerSlaveId')}>
                                                        <Input
                                                            value={encryptAutotunerSlaveId}
                                                            onChange={(e) => setEncryptAutotunerSlaveId(e.target.value)}
                                                            placeholder={t('automation.placeholders.autotunerSlaveId')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label={t('automation.fields.autotunerMcuId')}>
                                                        <Input
                                                            value={encryptAutotunerMcuId}
                                                            onChange={(e) => setEncryptAutotunerMcuId(e.target.value)}
                                                            placeholder={t('automation.placeholders.autotunerMcuId')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label={t('automation.fields.autotunerEcuId')}>
                                                        <Input
                                                            type="number"
                                                            value={encryptAutotunerEcuId}
                                                            onChange={(e) => setEncryptAutotunerEcuId(e.target.value)}
                                                            placeholder={t('automation.placeholders.autotunerEcuId')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item label={t('automation.fields.autotunerModelId')}>
                                                        <Input
                                                            type="number"
                                                            value={encryptAutotunerModelId}
                                                            onChange={(e) => setEncryptAutotunerModelId(e.target.value)}
                                                            placeholder={t('automation.placeholders.autotunerModelId')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    )}

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<SendOutlined />}
                                            onClick={handleEncryptSubmit}
                                            loading={isEncryptProcessing}
                                            disabled={!encryptFile || !encryptMethod}
                                            className="w-full"
                                        >
                                            {t('automation.buttons.encryptFile')}
                                        </Button>
                                    </Form.Item>

                                    {encryptErrorMessage && (
                                        <div className="text-red-500 text-sm mt-2">
                                            {encryptErrorMessage}
                                        </div>
                                    )}

                                    {encryptResponse && (
                                        <div className="mt-4">
                                            <Collapse
                                                items={[
                                                    {
                                                        key: '1',
                                                        label: t('automation.collapse.encryptionResponse'),
                                                        children: (
                                                            <div>
                                                                <div className="mb-4">
                                                                    <Button
                                                                        type="primary"
                                                                        icon={<DownloadOutlined />}
                                                                        onClick={handleEncryptDownload}
                                                                        className="mb-2"
                                                                    >
                                                                        {t('automation.buttons.downloadFile')}
                                                                    </Button>
                                                                    <div className="text-xs text-gray-500">
                                                                        {encryptResponse.name}
                                                                    </div>
                                                                </div>
                                                                <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                                                                    {JSON.stringify({
                                                                        timestamp: encryptResponse.timestamp,
                                                                        name: encryptResponse.name,
                                                                        encrypted_by: encryptResponse.encrypted_by,
                                                                        encrypted_by_master: encryptResponse.encrypted_by_master,
                                                                        hash: encryptResponse.hash,
                                                                        fileId: encryptResponse.fileId,
                                                                        downloadUrl: encryptResponse.downloadUrl
                                                                    }, null, 2)}
                                                                </pre>
                                                            </div>
                                                        ),
                                                    },
                                                ]}
                                                size="small"
                                            />
                                        </div>
                                    )}
                                </Form>
                            </Card>
                        </Col>

                    </Row>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title={t('automation.cards.processingStatus')} className="shadow-lg">
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="mb-0">{t('automation.sections.steps')}</h4>
                                <Button
                                    size="small"
                                    icon={<KeyOutlined />}
                                    onClick={handleApiKeyClick}
                                >
                                    {t('automation.buttons.bFlashApiKey')}
                                </Button>
                            </div>
                            <Steps
                                direction="vertical"
                                size="small"
                                items={steps}
                                className="mt-4"
                            />
                        </div>
                    </Card>
                </Col>
            </Row>
            
            <Row gutter={[24, 24]} className="mt-4">
                {analyseResponse?.projects && analyseResponse.projects.length > 0 && (
                    <Col xs={24}>
                        <Card title={t('automation.cards.matchedFiles')} className="shadow-lg">
                            <Table
                                columns={tableColumns}
                                dataSource={getTableData()}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => t('automation.table.totalFiles', { total }),
                                }}
                                scroll={{ x: 'max-content' }}
                                defaultExpandAllRows={true}
                            />
                        </Card>
                    </Col>
                )}
            </Row>

            {/* API Key Modal */}
            <Modal
                title={t('automation.modal.enterApiKey')}
                open={isApiKeyModalVisible}
                onOk={handleApiKeySave}
                onCancel={handleApiKeyCancel}
                okText={t('buttons.save')}
                cancelText={t('buttons.cancel')}
            >
                <p className="mb-4 text-gray-600">
                    {t('automation.modal.apiKeyDescription')}
                </p>
                <Input.TextArea
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={t('automation.placeholders.enterApiKey')}
                    rows={25}
                    className="mb-4"
                />
                {apiKey.trim() && (
                    <p className="text-sm text-green-600">
                        {t('automation.modal.apiKeyHint')}
                    </p>
                )}
            </Modal>

            {/* File Details Drawer */}
            <Drawer
                title={t('automation.drawer.fileDetails')}
                placement="right"
                width={800}
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
            >
                {selectedFileData && (
                    <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
                        {/* Basic Information */}
                        <Card title={t('automation.drawer.basicInformation')} size="small" style={{ marginBottom: '16px' }}>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label={t('automation.fields.id')}>{selectedFileData._id}</Descriptions.Item>
                                <Descriptions.Item label={t('automation.fields.projectReference')}>
                                    <Space>
                                        {selectedFileData.projectRef}
                                        <Tooltip title={t('automation.tooltips.copyProjectReference')}>
                                            <Button
                                                type="text"
                                                icon={<CopyOutlined />}
                                                size="small"
                                                onClick={() => copyToClipboard(selectedFileData.projectRef)}
                                            />
                                        </Tooltip>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('automation.fields.type')}>{selectedFileData.type}</Descriptions.Item>
                                <Descriptions.Item label={t('automation.fields.version')}>{selectedFileData.version}</Descriptions.Item>
                                {selectedFileData.tags && selectedFileData.tags.length > 0 && (
                                    <Descriptions.Item label={t('automation.fields.tags')}>
                                        <Space size={[0, 8]} wrap>
                                            {selectedFileData.tags.map((tag: string, index: number) => (
                                                <Tag key={index} color="green">
                                                    {tag}
                                                </Tag>
                                            ))}
                                        </Space>
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={t('automation.fields.status')}>
                                    <Tag color={getStatusColor(selectedFileData.status)}>
                                        {getStatusText(selectedFileData.status)}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('automation.fields.createdAt')}>
                                    {new Date(selectedFileData.createdAt).toLocaleString()}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('automation.fields.updatedAt')}>
                                    {new Date(selectedFileData.updatedAt).toLocaleString()}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('automation.fields.binaryAvailable')}>
                                    {selectedFileData.binaryPath ? (
                                        <Tag color="green">{t('automation.yes')}</Tag>
                                    ) : (
                                        <Tag color="red">{t('automation.no')}</Tag>
                                    )}
                                </Descriptions.Item>
                                {selectedFileData.downloadUrl && (
                                    <Descriptions.Item label={t('automation.fields.download')}>
                                        <Button
                                            type="primary"
                                            icon={<DownloadOutlined />}
                                            onClick={handleFileDownload}
                                        >
                                            {t('automation.buttons.downloadFile')}
                                        </Button>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>

                        {/* Vehicle Information */}
                        <Card title={t('automation.drawer.vehicleInformation')} size="small" style={{ marginBottom: '16px' }}>
                            <Descriptions bordered column={2} size="small">
                                {selectedFileData.vehicleType && (
                                    <Descriptions.Item label={t('automation.fields.vehicleType')}>{selectedFileData.vehicleType}</Descriptions.Item>
                                )}
                                {selectedFileData.vehicleProducer && (
                                    <Descriptions.Item label={t('automation.fields.vehicleProducer')}>{selectedFileData.vehicleProducer}</Descriptions.Item>
                                )}
                                {selectedFileData.vehicleSeries && (
                                    <Descriptions.Item label={t('automation.fields.vehicleSeries')}>{selectedFileData.vehicleSeries}</Descriptions.Item>
                                )}
                                {selectedFileData.vehicleBuild && (
                                    <Descriptions.Item label={t('automation.fields.vehicleBuild')}>{selectedFileData.vehicleBuild}</Descriptions.Item>
                                )}
                                {selectedFileData.vehicleModel && (
                                    <Descriptions.Item label={t('automation.fields.vehicleModel')}>{selectedFileData.vehicleModel}</Descriptions.Item>
                                )}
                                {selectedFileData.vehicleModelyear && (
                                    <Descriptions.Item label={t('automation.fields.modelYear')}>{selectedFileData.vehicleModelyear}</Descriptions.Item>
                                )}
                                {selectedFileData.userdef1 && (
                                    <Descriptions.Item label={t('automation.fields.userDef1')}>{selectedFileData.userdef1}</Descriptions.Item>
                                )}
                                {selectedFileData.userdef2 && (
                                    <Descriptions.Item label={t('automation.fields.userDef2')}>{selectedFileData.userdef2}</Descriptions.Item>
                                )}
                                {selectedFileData.userdef4 && (
                                    <Descriptions.Item label={t('automation.fields.userDef4')}>{selectedFileData.userdef4}</Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>

                        {/* ECU Information */}
                        <Card title={t('automation.drawer.ecuInformation')} size="small" style={{ marginBottom: '16px' }}>
                            <Descriptions bordered column={2} size="small">
                                {selectedFileData.ecuUse && (
                                    <Descriptions.Item label={t('automation.fields.ecuUse')}>{selectedFileData.ecuUse}</Descriptions.Item>
                                )}
                                {selectedFileData.ecuProducer && (
                                    <Descriptions.Item label={t('automation.fields.ecuProducer')}>{selectedFileData.ecuProducer}</Descriptions.Item>
                                )}
                                {selectedFileData.ecuBuild && (
                                    <Descriptions.Item label={t('automation.fields.ecuBuild')}>{selectedFileData.ecuBuild}</Descriptions.Item>
                                )}
                                {selectedFileData.ecuProdNr && (
                                    <Descriptions.Item label={t('automation.fields.ecuProdNr')}>{selectedFileData.ecuProdNr}</Descriptions.Item>
                                )}
                                {selectedFileData.ecuStgNr && (
                                    <Descriptions.Item label={t('automation.fields.ecuStgNr')}>{selectedFileData.ecuStgNr}</Descriptions.Item>
                                )}
                                {selectedFileData.ecuSoftwareVersion && (
                                    <Descriptions.Item label={t('automation.fields.softwareVersion')}>{selectedFileData.ecuSoftwareVersion}</Descriptions.Item>
                                )}
                                {selectedFileData.engineType && (
                                    <Descriptions.Item label={t('automation.fields.engineType')}>{selectedFileData.engineType}</Descriptions.Item>
                                )}
                                {selectedFileData.engineDisplacement && (
                                    <Descriptions.Item label={t('automation.fields.engineDisplacement')}>{selectedFileData.engineDisplacement}</Descriptions.Item>
                                )}
                                {selectedFileData.engineTransmission && (
                                    <Descriptions.Item label={t('automation.fields.transmission')}>{selectedFileData.engineTransmission}</Descriptions.Item>
                                )}
                                {selectedFileData.outputPS && (
                                    <Descriptions.Item label={t('automation.fields.outputPS')}>{selectedFileData.outputPS}</Descriptions.Item>
                                )}
                                {selectedFileData.outputKW && (
                                    <Descriptions.Item label={t('automation.fields.outputKW')}>{selectedFileData.outputKW}</Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>

                        {/* Analysis Information */}
                        <Card title={t('automation.drawer.analysisInformation')} size="small" style={{ marginBottom: '16px' }}>
                            <Descriptions bordered column={2} size="small">
                                <Descriptions.Item label={t('automation.fields.status')}>
                                    <Tag color={getStatusColor(selectedFileData.status)}>
                                        {getStatusText(selectedFileData.status)}
                                    </Tag>
                                </Descriptions.Item>
                                {selectedFileData.analyzedAt && (
                                    <Descriptions.Item label={t('automation.fields.analyzedAt')}>
                                        {new Date(selectedFileData.analyzedAt).toLocaleString()}
                                    </Descriptions.Item>
                                )}
                                {selectedFileData.error && (
                                    <Descriptions.Item label={t('automation.fields.error')} span={2}>
                                        <div style={{
                                            backgroundColor: '#fff2f0',
                                            border: '1px solid #ffccc7',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            color: '#ff4d4f'
                                        }}>
                                            {selectedFileData.error}
                                        </div>
                                    </Descriptions.Item>
                                )}
                                {selectedFileData.controlUnitFamily && (
                                    <Descriptions.Item label={t('automation.fields.controlUnitFamily')}>
                                        <Tag color="purple">{selectedFileData.controlUnitFamily}</Tag>
                                    </Descriptions.Item>
                                )}
                                {selectedFileData.isHiLo !== undefined && (
                                    <Descriptions.Item label={t('automation.fields.hiLoByteOrder')}>
                                        <Tag color={selectedFileData.isHiLo ? "green" : "orange"}>
                                            {selectedFileData.isHiLo ? t('automation.highByteFirst') : t('automation.lowByteFirst')}
                                        </Tag>
                                    </Descriptions.Item>
                                )}
                                {selectedFileData.blocks && selectedFileData.blocks.length > 0 && (
                                    <Descriptions.Item label={t('automation.fields.totalBlocks')}>
                                        <Badge count={selectedFileData.blocks.length} style={{ backgroundColor: token.colorInfo }} />
                                    </Descriptions.Item>
                                )}
                                {selectedFileData.blocks && selectedFileData.blocks.length > 0 && (
                                    <Descriptions.Item label={t('automation.fields.blockTypes')}>
                                        <Space wrap>
                                            {Array.from(new Set(selectedFileData.blocks.map((b: any) => b.type))).map((type: any) => (
                                                <Tag key={type} color="blue">{t('automation.blockType', { type })}</Tag>
                                            ))}
                                        </Space>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>

                        {/* Project Information */}
                        <Card title={t('automation.drawer.projectInformation')} size="small" style={{ marginBottom: '16px' }}>
                            <Descriptions bordered column={2} size="small">
                                {selectedFileData.readingHardware && (
                                    <Descriptions.Item label={t('automation.fields.readingHardware')}>{selectedFileData.readingHardware}</Descriptions.Item>
                                )}
                                {selectedFileData.projectType && (
                                    <Descriptions.Item label={t('automation.fields.projectType')}>{selectedFileData.projectType}</Descriptions.Item>
                                )}
                                {selectedFileData.numVersions && (
                                    <Descriptions.Item label={t('automation.fields.numberOfVersions')}>{selectedFileData.numVersions}</Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>

                        {/* Blocks Information */}
                        <Card
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>{t('automation.drawer.blocksInformation')}</span>
                                    {selectedFileData.blocks && selectedFileData.blocks.length > 0 && (
                                        <Badge count={selectedFileData.blocks.length} style={{ backgroundColor: token.colorInfo }} />
                                    )}
                                </div>
                            }
                            size="small"
                        >
                            {selectedFileData.blocks && selectedFileData.blocks.length > 0 ? (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {selectedFileData.blocks.map((block: any, index: number) => (
                                        <Card
                                            key={index}
                                            size="small"
                                            style={{
                                                marginBottom: '8px',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '6px'
                                            }}
                                            title={
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: 'bold' }}>{t('automation.block', { number: index + 1 })}</span>
                                                    <Tag color="blue">{t('automation.blockType', { type: block.type })}</Tag>
                                                </div>
                                            }
                                        >
                                            <Descriptions size="small" column={2}>
                                                <Descriptions.Item label={t('automation.fields.blockId')}>
                                                    <code style={{
                                                        backgroundColor: '#f5f5f5',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        fontSize: '12px'
                                                    }}>
                                                        {block.id}
                                                    </code>
                                                </Descriptions.Item>
                                                <Descriptions.Item label={t('automation.fields.size')}>
                                                    <Tag color="green">{block.length} {t('automation.bytes')}</Tag>
                                                </Descriptions.Item>
                                                {(block.physicalAddress !== undefined || block.endblockPhysicalAddress !== undefined) && (
                                                    <Descriptions.Item label={t('automation.fields.physicalAddress')}>
                                                        <Space>
                                                            {block.physicalAddress !== undefined && (
                                                                <Tag color="blue">0x{Number(block.physicalAddress).toString(16).toUpperCase()}</Tag>
                                                            )}
                                                            {block.physicalAddress !== undefined && block.endblockPhysicalAddress !== undefined && (
                                                                <span style={{ color: token.colorTextTertiary }}>-</span>
                                                            )}
                                                            {block.endblockPhysicalAddress !== undefined && (
                                                                <Tag color="purple">0x{Number(block.endblockPhysicalAddress).toString(16).toUpperCase()}</Tag>
                                                            )}
                                                        </Space>
                                                    </Descriptions.Item>
                                                )}
                                                {(block.logicalAddress !== undefined || block.endblockLogicalAddress !== undefined) && (
                                                    <Descriptions.Item label={t('automation.fields.logicalAddress')}>
                                                        <Space>
                                                            {block.logicalAddress !== undefined && (
                                                                <Tag color="cyan">0x{Number(block.logicalAddress).toString(16).toUpperCase()}</Tag>
                                                            )}
                                                            {block.logicalAddress !== undefined && block.endblockLogicalAddress !== undefined && (
                                                                <span style={{ color: token.colorTextTertiary }}>-</span>
                                                            )}
                                                            {block.endblockLogicalAddress !== undefined && (
                                                                <Tag color="magenta">0x{Number(block.endblockLogicalAddress).toString(16).toUpperCase()}</Tag>
                                                            )}
                                                        </Space>
                                                    </Descriptions.Item>
                                                )}
                                                {block.physicalToLogicalOffset !== undefined && (
                                                    <Descriptions.Item label={t('automation.fields.physicalToLogicalOffset')}>
                                                        <Tag color="orange">{block.physicalToLogicalOffset}</Tag>
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item label={t('automation.fields.hash')} span={2}>
                                                    <div style={{
                                                        backgroundColor: '#f5f5f5',
                                                        padding: '4px 8px',
                                                        borderRadius: '3px',
                                                        fontSize: '10px',
                                                        fontFamily: 'monospace',
                                                        wordBreak: 'break-all',
                                                        maxWidth: '100%'
                                                    }}>
                                                        {block.hash}
                                                    </div>
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    color: token.colorTextTertiary,
                                    fontStyle: 'italic'
                                }}>
                                    {t('automation.drawer.noBlocksInformation')}
                                </div>
                            )}
                        </Card>
                    </div>
                )}
            </Drawer>
        </div>
    );
};
